import { type RoleApp, roleSchema } from '@carte/contrats/commun';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChartColumn,
  CircleHelp,
  Compass,
  Map as IconeCarte,
  Route,
  Settings,
  ShieldCheck,
  ShieldEllipsis,
  TriangleAlert,
  Upload,
  Users,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';
import { CLE_HISTORIQUE_IMPORT, chargerHistoriqueImport } from '../admin/import/requetes';
import { ErreurApi, obtenir } from '../api';
import { BoutonRecherche } from '../recherche/BoutonRecherche';
import { BandeauHorsLigne } from './BandeauHorsLigne';
import { BoutonDeconnexion } from './BoutonDeconnexion';
import { adresseConnexion, ModaleReconnexion } from './ModaleReconnexion';
import { marquerRedirectionConnexion } from './session';

interface Entree {
  to:
    | '/'
    | '/proches'
    | '/tournees'
    | '/tableau-de-bord'
    | '/revue'
    | '/admin/systeme'
    | '/admin/import'
    | '/admin/utilisateurs';
  libelle: string;
  icone: ReactNode;
  /** Rôles qui voient l'entrée ; tous si absent. */
  roles?: readonly RoleApp[];
}

const ENTREES: Entree[] = [
  { to: '/', libelle: 'Carte', icone: <IconeCarte size={20} aria-hidden /> },
  { to: '/proches', libelle: 'Autour de moi', icone: <Compass size={20} aria-hidden /> },
  { to: '/tournees', libelle: 'Tournées', icone: <Route size={20} aria-hidden /> },
  { to: '/tableau-de-bord', libelle: 'Tableau de bord', icone: <ChartColumn size={20} aria-hidden /> },
  {
    to: '/revue',
    libelle: 'Revue',
    icone: <ShieldCheck size={20} aria-hidden />,
    roles: ['admin', 'backoffice'],
  },
  {
    to: '/admin/import',
    libelle: 'Import',
    icone: <Upload size={20} aria-hidden />,
    roles: ['admin', 'backoffice'],
  },
  { to: '/admin/systeme', libelle: 'Système', icone: <Settings size={20} aria-hidden />, roles: ['admin'] },
  {
    to: '/admin/utilisateurs',
    libelle: 'Utilisateurs',
    icone: <Users size={20} aria-hidden />,
    roles: ['admin'],
  },
];

/** Entrées de navigation visibles pour un rôle ; sans rôle connu, seules les entrées communes. */
export function entreesPour(role: RoleApp | null): Entree[] {
  return ENTREES.filter((e) => !e.roles || (role !== null && e.roles.includes(role)));
}

/** Nombre d'onglets au plus dans la barre du bas sur mobile. */
export const MAX_ONGLETS_MOBILE = 5;

/**
 * Répartit les entrées de la barre mobile : au-delà de MAX_ONGLETS_MOBILE, les écrans réservés à
 * certains rôles (revue et administration) passent dans un menu « Admin », qui compte pour un
 * onglet. Sur bureau, la barre latérale les montre tous.
 */
export function repartirOnglets(entrees: Entree[]): { onglets: Entree[]; menu: Entree[] } {
  if (entrees.length <= MAX_ONGLETS_MOBILE) return { onglets: entrees, menu: [] };
  return {
    onglets: entrees.filter((e) => !e.roles),
    menu: entrees.filter((e) => e.roles),
  };
}

/**
 * L'entrée mène-t-elle à l'écran affiché ? On compare le chemin seul, jamais la recherche : la carte
 * écrit son cadrage dans l'URL (?autour=..., ?vue=...) et la fiche vit sous /clients/:id, dans la
 * même mise en page que `/`, donc sous l'onglet « Carte ». Les autres entrées couvrent leurs sous-écrans
 * (/tournees/:id sous « Tournées »).
 */
export function entreeActive(to: Entree['to'], chemin: string): boolean {
  if (to === '/') return chemin === '/' || chemin.startsWith('/clients/');
  return chemin === to || chemin.startsWith(`${to}/`);
}

/** État actif d'un lien de navigation, imposé au Link quel que soit son propre calcul. */
const ETAT_ACTIF = { className: 'active', 'aria-current': 'page', 'data-status': 'active' } as const;
const ETAT_INACTIF = {} as const;

const CLASSE_ONGLET =
  'cible flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1 text-center text-xs leading-tight text-doux lg:flex-none lg:py-3 [&.active]:text-accent';

/** Entrée « Admin » de la barre mobile : ouvre la liste des écrans d'administration. */
export function MenuAdmin({ entrees, actif }: { entrees: Entree[]; actif: boolean }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <Popover.Root open={ouvert} onOpenChange={setOuvert}>
      <Popover.Trigger asChild>
        <button
          type="button"
          // Classe `active` comme les liens d'onglet : le sélecteur [&.active]:text-accent de
          // CLASSE_ONGLET l'emporte sur text-doux, ce que ne fait pas un text-accent ajouté à côté.
          className={`${CLASSE_ONGLET} lg:hidden ${actif ? 'active' : ''}`}
          aria-current={actif ? 'page' : undefined}
        >
          <ShieldEllipsis size={20} aria-hidden />
          <span>Admin</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="surgissement-vif z-50 flex min-w-48 origin-(--radix-popover-content-transform-origin) flex-col rounded-lg border border-bord bg-surface p-1 text-texte shadow-xl"
        >
          <nav aria-label="Administration" className="flex flex-col">
            {entrees.map((e) => (
              <Link
                key={e.to}
                to={e.to}
                onClick={() => setOuvert(false)}
                className="cible flex items-center gap-3 rounded-lg px-3 text-sm hover:bg-fond [&.active]:text-accent"
              >
                {e.icone}
                {e.libelle}
              </Link>
            ))}
          </nav>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const moiSchema = z.object({ role: roleSchema });

/** Rôle de l'utilisateur connecté, null tant qu'il est inconnu (chargement, hors ligne, anonyme). */
function useRole(): { role: RoleApp | null; sansSession: boolean } {
  const { data, error } = useQuery({
    queryKey: ['moi'],
    queryFn: () => obtenir('/api/moi', moiSchema),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    role: data?.role ?? null,
    sansSession: error instanceof ErreurApi && error.statut === 401,
  };
}

/** Rappel d'import (SPEC 4) : visible partout pour qui peut importer, sauf sur l'écran d'import lui-même. */
function RappelImport() {
  const { data } = useQuery({
    queryKey: CLE_HISTORIQUE_IMPORT,
    queryFn: chargerHistoriqueImport,
    staleTime: 10 * 60_000,
    retry: false,
  });
  if (!data?.rappel) return null;
  return (
    // Le rôle status va sur un conteneur : posé sur le lien, il en effaçait le rôle et le nom (axe link-name).
    <div role="status" className="border-b border-alerte bg-surface">
      <Link to="/admin/import" className="flex items-center gap-2 px-4 py-2 text-sm">
        <TriangleAlert size={16} className="shrink-0 text-alerte" aria-hidden />
        {data.ageJours === null
          ? "Aucun import n'a encore été fait. Importez un instantané."
          : `Le dernier import date de ${data.ageJours} jours. Mettre les données à jour.`}
      </Link>
    </div>
  );
}

/**
 * Coquille de l'application : barre latérale à gauche sur bureau (1024 px et plus), barre d'onglets
 * en bas sur mobile (SPEC 3), entrées selon le rôle, recherche, rappel d'import, bandeau hors ligne
 * et modale de reconnexion. Une arrivée sans session (401 au chargement) renvoie vers /connexion
 * avec l'adresse de retour.
 */
export function Coquille({ children }: { children: ReactNode }) {
  const { role, sansSession } = useRole();
  const chemin = useRouterState({ select: (s) => s.location.pathname });
  const surConnexion = chemin.startsWith('/connexion');
  const entrees = entreesPour(role);
  const { menu } = repartirOnglets(entrees);
  const dansMenu = new Set(menu.map((e) => e.to));
  const peutImporter = role === 'admin' || role === 'backoffice';

  useEffect(() => {
    if (sansSession && !surConnexion) {
      marquerRedirectionConnexion();
      window.location.replace(adresseConnexion());
    }
  }, [sansSession, surConnexion]);

  return (
    <div className="flex h-full flex-col">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2"
      >
        Aller au contenu
      </a>
      <BandeauHorsLigne />
      {!surConnexion && (
        <header className="flex shrink-0 items-center justify-end gap-2 border-b border-bord bg-surface px-3 py-1.5">
          <BoutonRecherche />
          <Link
            to="/aide"
            className="cible inline-flex items-center justify-center gap-2 rounded-lg px-3 text-sm text-doux hover:text-texte [&.active]:text-accent"
          >
            <CircleHelp size={18} aria-hidden />
            <span className="sr-only sm:not-sr-only">Aide</span>
          </Link>
          <BoutonDeconnexion />
        </header>
      )}
      {peutImporter && !surConnexion && !chemin.startsWith('/admin/import') ? <RappelImport /> : null}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {!surConnexion && (
          <nav
            aria-label="Navigation principale"
            className="order-2 flex shrink-0 justify-around border-t border-bord bg-surface pb-[env(safe-area-inset-bottom)] lg:order-1 lg:w-20 lg:flex-col lg:justify-start lg:gap-1 lg:border-t-0 lg:border-r lg:pt-2 lg:pb-0"
          >
            {entrees.map((e) => {
              // L'état actif vient de entreeActive (chemin seul) : le calcul du Link, lui, compare la
              // recherche et ne voit pas /clients/:id sous `/`. activeOptions le garde cohérent avec
              // le nôtre (il ne marque jamais actif ce que entreeActive refuse).
              const etat = entreeActive(e.to, chemin) ? ETAT_ACTIF : ETAT_INACTIF;
              return (
                <Link
                  key={e.to}
                  to={e.to}
                  activeOptions={{ exact: e.to === '/', includeSearch: false }}
                  activeProps={etat}
                  inactiveProps={etat}
                  className={`${CLASSE_ONGLET} ${dansMenu.has(e.to) ? 'hidden lg:flex' : ''}`}
                >
                  {e.icone}
                  <span>{e.libelle}</span>
                </Link>
              );
            })}
            {menu.length > 0 && (
              <MenuAdmin entrees={menu} actif={menu.some((e) => entreeActive(e.to, chemin))} />
            )}
          </nav>
        )}
        {/* `relative` : bloc conteneur des éléments positionnés en absolu (textes `sr-only` des
            écrans) ; sans lui, c'est la racine du document qui les reçoit, et la fenêtre entière
            défile en plus de la zone de contenu (double barre de défilement). */}
        <main id="contenu" className="relative order-1 min-h-0 flex-1 overflow-y-auto lg:order-2">
          {children}
        </main>
      </div>
      <ModaleReconnexion />
    </div>
  );
}
